import dayjs from 'dayjs';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import useVirtual, { LoadMoreEvent } from 'react-cool-virtual';
import EventTitle from './EventTitle';
import {
  ALARM_ID,
  END_TYPE,
  EVENT_ID,
  MIDNIGHT_ID,
  PARKING_TYPE,
  START_TYPE,
  WAYPOINT_ID,
} from '../constants';
import { rideInfoRecordsAtom } from '../TripsOverviewState';
import { getDay, getInitPage, loadAnotherEvents } from '../TripsOverviewService';

interface IEventsProps {
  rideId: string;
  customerId: number;
  recordsCount: number;
  start: string;
  isAnonym: boolean;
  datacyIndex: number;
}

function Events(props: IEventsProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [isFirstLaodFromRideRecord, setIsFirstLaodFromRideRecord] = React.useState(true);
  const [loader, setLoader] = React.useState(false);
  const [rideInfoRecords, setRideInfoRecords] = useRecoilState(rideInfoRecordsAtom(props.rideId));
  const endEventIndex = rideInfoRecords.findIndex((i) => i.type === END_TYPE);
  const [currentPage, setCurrentPage] = React.useState<number>(
    getInitPage(rideInfoRecords?.length)
  );
  const [actualSizeOfRecords, setActualSizeOfRecords] = React.useState<number>(0);
  const [loadedRecords, setLoadedRecords] = React.useState<number>(0);

  const isItemLoadedArr: boolean[] = React.useMemo(() => [], []);
  isItemLoadedArr[props.recordsCount] = true;

  React.useEffect(() => {
    if (rideInfoRecords.length > 0) {
      setLoadedRecords(rideInfoRecords.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnotherEvents = React.useCallback(
    async (event: LoadMoreEvent) => {
      if (!loader) {
        if (!isFirstLaodFromRideRecord) {
          const { loadIndex } = event;
          isItemLoadedArr[loadIndex] = true;

          if (rideInfoRecords?.length === props.recordsCount) {
            isItemLoadedArr[loadIndex] = false;
            return;
          }
          setLoader(true);
          const result = await loadAnotherEvents(
            props.rideId,
            String(props.customerId),
            currentPage
          );
          setLoader(false);

          if (result && rideInfoRecords.length > 0) {
            setCurrentPage((oldPage) => oldPage + 1);
            setActualSizeOfRecords(result.length - 1);
            const newRecords = rideInfoRecords.concat(result);

            setRideInfoRecords(newRecords);
          }
        } else {
          setIsFirstLaodFromRideRecord(false);
        }
      }
    },
    [
      isFirstLaodFromRideRecord,
      currentPage,
      isItemLoadedArr,
      loader,
      props.customerId,
      props.recordsCount,
      props.rideId,
      rideInfoRecords,
      setRideInfoRecords,
    ]
  );

  const { outerRef, innerRef, items } = useVirtual<HTMLUListElement | HTMLLIElement>({
    itemCount: rideInfoRecords.length,
    loadMoreCount: actualSizeOfRecords,
    isItemLoaded: (loadIndex) => isItemLoadedArr[loadIndex],
    loadMore: (event) => handleAnotherEvents(event),
  });

  const handleIconColor = (type: string): string => {
    let iconClass = 'text-gray';
    switch (type) {
      case WAYPOINT_ID:
        iconClass = 'text-green-dark';
        break;
      case ALARM_ID:
        iconClass = 'text-c-red';
        break;
      case EVENT_ID:
        iconClass = 'text-violet-dark';
        break;
      case MIDNIGHT_ID:
        iconClass = 'text-blue-dark';
        break;
      case PARKING_TYPE:
        iconClass = 'text-blue';
        break;
      default:
    }
    return iconClass;
  };

  const handlePointColor = (type: string) => {
    if (type === START_TYPE) {
      return 'bullet-green';
    }
    if (type === END_TYPE) {
      return 'bullet-red';
    }
    if (type === MIDNIGHT_ID) {
      return 'midnight';
    }
    return '';
  };

  const loading = (): JSX.Element => (
    <h6
      className=" text-black"
      style={{
        display: 'flex',
        justifyContent: 'center',
        fontWeight: 'bold',
      }}
    >
      {f({ id: 'waypoints.loading' })}
    </h6>
  );

  const getDatacyIndex = (index: number, type: string) => {
    if (type === START_TYPE) {
      return 'start';
    }
    if (type === END_TYPE) {
      return 'end';
    }
    if (endEventIndex < index) {
      return index - 1;
    }
    return index;
  };

  return (
    <>
      <div className="font-weight-bold my-2 ml-2">{f({ id: 'tripsOverview.events.title' })}</div>
      <div
        className="w-waypoints-timeline pt-0 pb-2"
        ref={outerRef as React.LegacyRef<HTMLDivElement>}
        data-cy="ride-book-events"
      >
        <div className="text-gray-light fs-1 font-weight-bold ml-2 mb-2">
          {dayjs(props.start).format('DD.MM.YYYY')} - {f({ id: `date.${getDay(props.start)}` })}
        </div>
        {rideInfoRecords && rideInfoRecords.length > 0 && (
          <ul
            id="eventScrollBar"
            className="b-waypoints-timeline b-drive-color-info pr-4"
            ref={innerRef as React.LegacyRef<HTMLUListElement>}
          >
            {items.length ? (
              items.map(({ index, measureRef }) => {
                const showLoading =
                  index === rideInfoRecords.length - 1 &&
                  rideInfoRecords.length + loadedRecords < props.recordsCount;

                return (
                  <React.Fragment key={`rideInfoRecords-events-${index}`}>
                    {rideInfoRecords[index] && (
                      <li
                        data-cy={`row-${props.datacyIndex + 1}-ride_info-${getDatacyIndex(
                          index,
                          rideInfoRecords[index].type
                        )}`}
                        className={`${handleIconColor(
                          rideInfoRecords[index].type
                        )} ${handlePointColor(rideInfoRecords[index].type)}`}
                        ref={measureRef}
                      >
                        <EventTitle
                          index={index}
                          rideId={props.rideId}
                          title={rideInfoRecords[index].title}
                          type={rideInfoRecords[index].type}
                          activatedAt={rideInfoRecords[index].activatedAt}
                          duration={rideInfoRecords[index].duration}
                          isLast={rideInfoRecords.length === index + 1}
                          isAnonym={props.isAnonym}
                        />

                        {loader && showLoading && loading()}
                      </li>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <></>
            )}
          </ul>
        )}
      </div>
    </>
  );
}

export default React.memo(Events);
